<template>
  <div class="card">
    <div class="bg-blue-light p-1">
      <TitleButton
          :showBtn="false"
          title="Depreciation Journal"
      />
    </div>

    <ul class="nav nav-tabs px-2 pt-2" role="tablist">
      <li class="nav-item">
        <a
            :class="['nav-link', { active: isPendingTab }]"
            style="cursor: pointer"
            @click="onTabChange('pending')"
        >
          Pending JV
        </a>
      </li>
      <li class="nav-item">
        <a
            :class="['nav-link', { active: isCompleteTab }]"
            style="cursor: pointer"
            @click="onTabChange('complete')"
        >
          Complete JV
        </a>
      </li>
    </ul>

    <div class="px-2 mt-2">
      <div class="table-responsive table-min-height">
        <table class="table table-bordered">
          <thead>
          <tr>
            <th>Depn. Period</th>
            <th>Depn Process No</th>
            <th>Process Date</th>
            <th class="text-end">Total Depn Amount</th>
            <th v-if="isPendingTab">Status</th>
            <th v-if="isCompleteTab">Voucher No</th>
            <th>Action</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="run in runs" :key="run.id">
            <td>{{ run.format_start_date }} - {{ run.format_end_date }}</td>
            <td>{{ run.id }}</td>
            <td>{{ run.format_created_at }}</td>
            <td class="text-end">{{ formatAmount(run.amount) }}</td>
            <td v-if="isPendingTab">
              <span :class="statusClass(run.journal_status)">{{ statusLabel(run.journal_status) }}</span>
            </td>
            <td v-if="isCompleteTab">
              <span class="fw-bold">{{ run.voucher_no }}</span>
              <div class="text-muted small">{{ run.voucher_status }}</div>
            </td>
            <td>
              <button
                  v-if="run.can_create_journal"
                  class="btn btn-sm btn-outline-primary"
                  @click="goToCreate(run.id)"
              >
                Create JV
              </button>
              <button
                  v-else-if="run.ledger_master_id"
                  class="btn btn-sm btn-outline-success"
                  @click="goToCreate(run.id)"
              >
                View JV
              </button>
              <span v-else class="text-muted">{{ run.status }}</span>
            </td>
          </tr>
          </tbody>
        </table>
        <p v-if="!hasRuns" class="text-center mt-5">{{ emptyMessage }}</p>
      </div>
    </div>

    <div class="px-2 pb-2 mt-3">
      <Pagination :offset="offset" @onPageChange="onPageChange" ref="pagination"/>
    </div>

    <Loader v-if="loading"/>
  </div>
</template>

<script setup>
import {computed, onMounted, ref} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import {useStore} from 'vuex'
import TitleButton from '@/components/atom/TitleButton'
import Loader from '@/components/atom/LoaderComponent'
import Pagination from '@/components/atom/Pagination'
import handleAssetRegister from '@/services/modules/assetRegister/assetRegister'

const $route = useRoute()
const $router = useRouter()
const $store = useStore()

const {fetchDepreciationJournals} = handleAssetRegister()

const loading = ref(false)
const runs = ref([])
const offset = ref(20)
const activeTab = ref('pending')

const statusOptions = [
  {name: 'Pending', value: 'pending'},
  {name: 'Created', value: 'created'},
  {name: 'Reprocess', value: 'reprocess'}
]

const hasRuns = computed(() => runs.value.length > 0)
const isPendingTab = computed(() => activeTab.value === 'pending')
const isCompleteTab = computed(() => activeTab.value === 'complete')

const tabStatus = computed(() => {
  if (isPendingTab.value) return 'pending,reprocess'
  return 'created'
})

const emptyMessage = computed(() => {
  if (isPendingTab.value) return 'No depreciation run is waiting for a journal voucher'
  return 'No journal voucher has been created yet'
})

const formatAmount = (amount) => {
  if (amount === null || amount === undefined) return '0.00'
  return Number(amount).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})
}

const statusLabel = (status) => {
  const option = statusOptions.find(item => item.value === status)
  if (!option) return status
  return option.name
}

const statusClass = (status) => {
  if (status === 'created') return 'badge bg-success'
  if (status === 'reprocess') return 'badge bg-warning'
  return 'badge bg-secondary'
}

const getQuery = () => {
  let query = `?company_id=${$route.params.companyId}&offset=${offset.value}`
  query += `&journal_status=${tabStatus.value}`
  if (!$route.query.page) return query
  return `${query}&page=${$route.query.page}`
}

const getRuns = () => {
  loading.value = true
  fetchDepreciationJournals(getQuery())
      .then(res => {
        if (!res.status) {
          runs.value = []
          $store.commit('resetPagination')
          return
        }
        runs.value = res.data.data
        setPagination(res.data)
      })
      .finally(() => loading.value = false)
}

const setPagination = (data) => {
  if (!data.data.length) return
  $store.commit('setPaginationData', {
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page,
  })
}

const onPageChange = async (page) => {
  let routeQuery = Object.assign({}, $route.query)
  routeQuery.page = page
  await $router.push({path: $route.path, query: routeQuery})
  getRuns()
}

const onTabChange = async (tab) => {
  if (activeTab.value === tab) return
  activeTab.value = tab
  let routeQuery = Object.assign({}, $route.query)
  delete routeQuery.page
  await $router.push({path: $route.path, query: routeQuery})
  getRuns()
}

const goToCreate = (processTrackerId) => {
  $router.push({
    name: 'depreciation-journal-create',
    params: {
      companyId: $route.params.companyId,
      moduleId: $route.params.moduleId,
      menuId: $route.params.menuId,
      pageId: $route.params.pageId,
      processTrackerId: processTrackerId
    }
  })
}

onMounted(() => {
  getRuns()
})
</script>
